import React, { useEffect, useRef, useState, useMemo } from "react";
import { StationDropdownProps } from "../types";
import { scrollToShowDropdown } from "../../../../utils/dropdownScrollUtils";
import {
  StationDropdownContainer,
  StationDropdownContent,
  StationDropdownIcon,
  StationDropdownLabel,
  StationDropdownOption,
  StationDropdownOptions,
  StationDropdownPlaceholder,
  StationDropdownValue,
  StationDropdownValueText,
  SearchContainer,
  SearchIconWrapper,
  SearchInput,
  SearchResultsContainer,
  NoResultsMessage,
} from "../styles";
import { ReactComponent as DropdownDownIconSvg } from "../assets/dropdown-iterative-down.svg";
import { ReactComponent as DropdownUpIconSvg } from "../assets/dropdown-iterative-up.svg";
import { ReactComponent as SearchIcon } from "../../../common/assets/search.svg";

// Extracted functions for testing
export const sortStationsAlphabetically = (stations: Stop[]): Stop[] => {
  return [...stations].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, {
      sensitivity: "base",
    })
  );
};

export const filterStationsBySearchText = (
  stations: Stop[],
  searchText: string
): Stop[] => {
  if (!searchText.trim()) {
    return stations;
  }
  return stations.filter((station) =>
    (station.name || "").toLowerCase().includes(searchText.toLowerCase())
  );
};

export const StationDropdown: React.FC<StationDropdownProps> = ({
  stations,
  selectedStation,
  onStationSelect,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sort stations alphabetically by name
  const sortedStations = useMemo(() => {
    return sortStationsAlphabetically(stations);
  }, [stations]);

  // Filter stations based on search text
  const filteredStations = useMemo(() => {
    return filterStationsBySearchText(sortedStations, searchText);
  }, [sortedStations, searchText]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchText("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        // Focus input when opening dropdown
        setTimeout(() => {
          inputRef.current?.focus();
          // Check if dropdown options are visible and scroll if needed
          scrollToShowDropdown(dropdownRef);
        }, 100);
      } else {
        setSearchText("");
      }
    }
  };

  const handleStationSelect = (station: any) => {
    if (!disabled) {
      onStationSelect(station);
      setIsOpen(false);
      setSearchText("");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      setSearchText("");
    }
  };

  return (
    <StationDropdownContainer
      ref={dropdownRef}
      style={disabled ? { opacity: 0.5, backgroundColor: "#f5f5f5" } : {}}
    >
      <StationDropdownContent onClick={handleToggle}>
        {disabled ? (
          <StationDropdownValue>
            <StationDropdownValueText>
              {selectedStation ? selectedStation.name : "Select a station"}
            </StationDropdownValueText>
          </StationDropdownValue>
        ) : (
          <>
            <StationDropdownPlaceholder>
              <StationDropdownLabel>Station</StationDropdownLabel>
            </StationDropdownPlaceholder>
            <StationDropdownValue>
              <StationDropdownValueText>
                {selectedStation ? selectedStation.name : "Select a station"}
              </StationDropdownValueText>
            </StationDropdownValue>
          </>
        )}
      </StationDropdownContent>
      {!disabled && (
        <StationDropdownIcon onClick={handleToggle}>
          {isOpen ? <DropdownUpIconSvg /> : <DropdownDownIconSvg />}
        </StationDropdownIcon>
      )}

      {isOpen && !disabled && (
        <StationDropdownOptions data-dropdown-options>
          <SearchContainer>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <SearchInput
              ref={inputRef}
              type="text"
              placeholder="Search station"
              value={searchText}
              onChange={handleSearchChange}
              onKeyDown={handleInputKeyDown}
              onClick={(e) => e.stopPropagation()}
            />
          </SearchContainer>
          <SearchResultsContainer>
            {filteredStations.length > 0 ? (
              filteredStations.map((station: any) => (
                <StationDropdownOption
                  key={station.id}
                  onClick={() => handleStationSelect(station)}
                >
                  {station.name}
                </StationDropdownOption>
              ))
            ) : (
              <NoResultsMessage>
                {searchText ? "No stations found" : "No stations available"}
              </NoResultsMessage>
            )}
          </SearchResultsContainer>
        </StationDropdownOptions>
      )}
    </StationDropdownContainer>
  );
};
